import Bitmap from "../../bitmap";
import Color from "../../models/color";
import { BaseBlockPack } from "./BaseBlockPack";
import DefaultBlockPack from "./DefaultBlockPack";
import { drawStraightBorder } from "./utils/borderUtils";

// Import the cement texture image
// The file is at: s../core/engine/packs/blocks/cement/cement-texture.png
import cementTextureImage from "./cement/cement-texture.png";

export default class CementBlockPack extends BaseBlockPack {
  private static imageCache: HTMLImageElement | null = null;
  private static imageLoaded: boolean = false;

  constructor() {
    super();
    CementBlockPack.preloadImage();
  }

  /**
   * Preloads the cement texture image
   */
  private static preloadImage(): void {
    if (CementBlockPack.imageLoaded) return;

    const img = new Image();
    img.onload = () => {
      CementBlockPack.imageCache = img;
      CementBlockPack.imageLoaded = true;
      console.log(`[CementBlockPack] Successfully loaded cement texture`);
    };
    img.onerror = (error) => {
      console.error(`[CementBlockPack] Failed to load cement texture image.`);
      console.error(`[CementBlockPack] Import path resolved to: ${cementTextureImage}`);
      console.error(`[CementBlockPack] Expected file at: s../core/engine/packs/blocks/cement/cement-texture.png`);
      console.error(`[CementBlockPack] Error details:`, error);
      CementBlockPack.imageLoaded = true;
    };
    console.log(`[CementBlockPack] Attempting to load texture from: ${cementTextureImage}`);
    img.src = cementTextureImage;
  }

  /**
   * Gets the cement texture image
   */
  private getTextureImage(): HTMLImageElement | null {
    return CementBlockPack.imageCache;
  }

  drawBlock(
    bit: Bitmap,
    x: number,
    y: number,
    side: number,
    width: number,
    color: Color,
    to: number[]
  ): void {
    const textureImage = this.getTextureImage();

    if (textureImage && textureImage.complete && textureImage.naturalWidth > 0) {
      // Follow the exact same structure as DefaultBlockPack
      this.drawRawBlock(bit, x, y, side, width, color, textureImage);
      const borderWidth = this.getBorderWidth(width);
      this.drawBlockCorner(bit, x, y, side, width, borderWidth, color, to);
    } else {
      // Fallback to default drawn block if texture not loaded
      const defaultPack = new DefaultBlockPack();
      defaultPack.drawBlock(bit, x, y, side, width, color, to);
    }
  }

  /**
   * Draws the block with texture, following DefaultBlockPack's structure
   */
  private drawRawBlock(
    bit: Bitmap,
    x: number,
    y: number,
    side: number,
    width: number,
    color: Color,
    textureImage: HTMLImageElement
  ): void {
    let tx: number,
      tl: number,
      rx: number = 0,
      ry: number = 0,
      rw: number = width,
      rh: number = width;

    const bw = this.getBorderWidth(width);

    // Left border - same as DefaultBlockPack
    if (side & 1) {
      drawStraightBorder(bit, x, y, bw, width, color.shade(5));
      rx = bw;
      rw -= bw;
    }
    
    // Top border - same as DefaultBlockPack
    if (side & 2) {
      tx = side & 1 ? x + bw : x;
      bit.drawShape([
        x, y,
        x + width, y,
        x + width, y + bw,
        tx, y + bw
      ], color.shade(5));
      ry = bw;
      rh -= bw;
    }
    
    // Right border - same as DefaultBlockPack
    if (side & 4) {
      tx = side & 2 ? y + bw : y;
      bit.drawShape([
        x + width, y,
        x + width, y + width,
        x + width - bw, y + width,
        x + width - bw, tx
      ], color.shade(3));
      rw -= bw;
    }
    
    // Bottom border - same as DefaultBlockPack
    if (side & 8) {
      tx = side & 1 ? x + bw : x;
      tl = x - tx + width;
      if (side & 4) tl -= bw;
      bit.drawShape([
        x, y + width,
        x + width, y + width,
        tx + tl, y + width - bw,
        tx, y + width - bw
      ], color.shade(3));
      rh -= bw;
    }

    // Draw texture fill in the remaining area (same as DefaultBlockPack's fill)
    if (rw > 0 && rh > 0) {
      const ctx = bit.context;
      const fillX = x + rx;
      const fillY = y + ry;
      const fillW = rw;
      const fillH = rh;
      
      ctx.save();

      // Draw the grayscale texture resized to fit the fill area
      bit.drawImage(textureImage, fillX, fillY, fillW, fillH);

      // Colorize the grayscale texture by applying the block color
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = color.shade(4);
      ctx.fillRect(Math.round(fillX), Math.round(fillY), Math.round(fillW), Math.round(fillH));
      
      ctx.globalCompositeOperation = 'source-over';
      ctx.restore();
    }
  }
}

